// Before a payment invitation leaves the dashboard, the settlement is read
// again and the lease's payment orders are looked up, so a tenant never gets
// a second invitation for the same settlement (X36, decision D4 still open:
// this is the dashboard's stopgap, option C). It catches:
// - an invitation another tab or another user sent since this view loaded:
//   the settlement stored now links it;
// - an invitation whose link to the settlement was lost (the page reloaded
//   or the tab closed between creating the order and storing its link): a
//   live one-off order on the lease for the same amount, created since the
//   settlement was confirmed, that the settlement does not link;
// - a tenant marked paid by hand in the meantime.
// It cannot stop two tabs that both pass this check before either creates
// its order, an order the user cannot see in the payment-order list (it
// shows an order only to an account with a permission on it or with the
// owner or financial-manager role on its property), nor invitations sent
// from anywhere but the settlement screens. Only the server can (D4 = A/B).

import * as paymentOrderApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.api";
import { getPaymentOrdersQuery } from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.utils";
import * as settlementApi from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.api";
import { ISettlement } from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.types";
import { EPaymentOrderType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { uniq } from "lodash";
import { unwrapEntity } from "./apiResponse.utils";
import { toBrusselsIsoDay } from "./localDay.utils";
import { getRecipientName, IBilledShareLike } from "./payer.utils";
import { settlementFromApi } from "./settlement.mapper";
import { hasLinkedPayment } from "./settlementPaymentStatus.utils";
import { Settlement, SettlementAction } from "./types";

// The lease's orders the check reads at most. A lease has a handful of
// one-off orders; the newest come first.
const ORDERS_PER_LEASE = 100;

// The amount of an order matches the invitation within a cent: the amount
// is frozen incl. VAT and rounded once more when the form submits it.
const AMOUNT_TOLERANCE_CENTS = 1;

export enum EInviteBlockReason {
  // The settlement as stored now holds an invitation for this tenant.
  Invited = "invited",
  // The tenant was marked paid outside Rentio in the meantime.
  MarkedPaid = "marked_paid",
  // A payment order for this lease and amount that the settlement does not
  // link, created since the settlement was confirmed.
  UnlinkedOrder = "unlinked_order",
}

export interface IFoundOrder {
  id: number;
  amount: number;
  // Its Brussels calendar day, "YYYY-MM-DD".
  createdOn?: string;
}

export interface IInviteBlock {
  contractId: string;
  reason: EInviteBlockReason;
  order?: IFoundOrder;
}

// The fields of a payment order the check reads, as the v2 list returns it.
export interface ILeaseOrder {
  id: number;
  type?: string;
  amount?: number | string;
  contractId?: string | null;
  createdAt?: string | Date;
  completedAt?: string | Date | null;
}

export interface IInviteTarget {
  contractId: string;
  // What the invitation bills, incl. VAT.
  amount: number;
}

const toCents = (amount: number) => Math.round(amount * 100);

const isSameAmount = (left: number, right: number) =>
  Math.abs(toCents(left) - toCents(right)) <= AMOUNT_TOLERANCE_CENTS;

// The newest live one-off order on the target's lease for its amount that
// no action of the settlement links, created on or after the day it was
// confirmed. A completed order is left out: the list cannot tell one that
// was paid from one whose request was withdrawn, and taking the withdrawn
// one would refuse the invitation for good.
const findUnlinkedOrder = (params: {
  target: IInviteTarget;
  orders: ILeaseOrder[];
  linkedOrderIds: ReadonlySet<number>;
  confirmedOn?: string;
}): IFoundOrder | undefined => {
  const { target, orders, linkedOrderIds, confirmedOn } = params;
  const found = orders
    .filter(order => {
      const amount = Number(order.amount);
      const createdOn = toBrusselsIsoDay(order.createdAt);
      return (
        order.contractId === target.contractId &&
        order.type === EPaymentOrderType.OtherOneoff &&
        order.completedAt == null &&
        Number.isFinite(amount) &&
        isSameAmount(amount, target.amount) &&
        !linkedOrderIds.has(Number(order.id)) &&
        (!confirmedOn || (createdOn != null && createdOn >= confirmedOn))
      );
    })
    .sort((left, right) => Number(right.id) - Number(left.id))[0];
  return found
    ? {
        id: Number(found.id),
        amount: Number(found.amount),
        createdOn: toBrusselsIsoDay(found.createdAt),
      }
    : undefined;
};

// Why each target may not be invited (again). `actions` are the actions of
// the settlement as stored now, without the links of payments withdrawn
// under Payments (see withoutWithdrawnLink): such a tenant may be invited
// anew. `linkedOrderIds` are every order any of its actions links, withdrawn
// or not, and `endedOrderIds` the orders known to have ended: neither is an
// order whose link got lost.
export const findInviteBlocks = (params: {
  targets: IInviteTarget[];
  actions: SettlementAction[];
  linkedOrderIds: number[];
  endedOrderIds?: ReadonlySet<number>;
  confirmedAt?: string;
  orders: ILeaseOrder[];
}): IInviteBlock[] => {
  const { targets, actions, linkedOrderIds, endedOrderIds, confirmedAt } =
    params;
  const knownOrderIds = new Set([
    ...linkedOrderIds.map(Number),
    ...(endedOrderIds ?? []),
  ]);
  const confirmedOn = confirmedAt ? confirmedAt.slice(0, 10) : undefined;

  return targets.flatMap((target): IInviteBlock[] => {
    const action = actions.find(
      candidate => candidate.contractId === target.contractId,
    );
    if (action && hasLinkedPayment(action)) {
      return [
        { contractId: target.contractId, reason: EInviteBlockReason.Invited },
      ];
    }
    if (action?.manualPaidAt) {
      return [
        {
          contractId: target.contractId,
          reason: EInviteBlockReason.MarkedPaid,
        },
      ];
    }
    const order = findUnlinkedOrder({
      target,
      orders: params.orders,
      linkedOrderIds: knownOrderIds,
      confirmedOn,
    });
    return order
      ? [
          {
            contractId: target.contractId,
            reason: EInviteBlockReason.UnlinkedOrder,
            order,
          },
        ]
      : [];
  });
};

// The text that explains a block, with the values it fills in.
export const getInviteBlockText = (params: {
  block: IInviteBlock;
  recipient: string;
  formatAmount: (amount: number) => string;
  formatDay: (isoDay: string) => string;
}): { key: string; values: Record<string, string> } => {
  const { block, recipient, formatAmount, formatDay } = params;
  switch (block.reason) {
    case EInviteBlockReason.Invited:
      return {
        key: "settlement.invite_blocked.invited",
        values: { tenant: recipient },
      };
    case EInviteBlockReason.MarkedPaid:
      return {
        key: "settlement.invite_blocked.marked_paid",
        values: { tenant: recipient },
      };
    case EInviteBlockReason.UnlinkedOrder:
      return {
        key: "settlement.invite_blocked.unlinked_order",
        values: {
          tenant: recipient,
          order: String(block.order?.id ?? ""),
          amount: block.order ? formatAmount(block.order.amount) : "",
          date: block.order?.createdOn ? formatDay(block.order.createdOn) : "",
        },
      };
  }
};

// The live payment orders of these leases (deleted ones never come back),
// one-off ones only, newest first.
export const fetchLeaseOneOffOrders = async (
  contractIds: string[],
): Promise<ILeaseOrder[]> => {
  const responses = await Promise.all(
    uniq(contractIds).map(contractId =>
      paymentOrderApi.getPaged({
        query: getPaymentOrdersQuery({
          contractId,
          limit: ORDERS_PER_LEASE,
          filter: { type: { eq: EPaymentOrderType.OtherOneoff } },
          sort: [
            { field: "createdAt", method: "DESC" },
            { field: "id", method: "DESC" },
          ],
        }),
      }),
    ),
  );
  return responses.flatMap(
    response => (response.data?.data?.items ?? []) as ILeaseOrder[],
  );
};

// What the dialog says about the blocked targets, one sentence each,
// followed by the texts of `extraKeys`. Named after who the invitation
// bills (the payer), as everywhere a payment is named.
export const getInviteBlockedInfo = (params: {
  blocks: IInviteBlock[];
  targets: (IInviteTarget & IBilledShareLike)[];
  formatAmount: (amount: number) => string;
  formatDay: (isoDay: string) => string;
  extraKeys?: string[];
}): string => {
  const { blocks, targets, formatAmount, formatDay, extraKeys = [] } = params;
  return [
    ...blocks.map(block => {
      const target = targets.find(
        candidate => candidate.contractId === block.contractId,
      );
      const { key, values } = getInviteBlockText({
        block,
        recipient: target ? getRecipientName(target) : "",
        formatAmount,
        formatDay,
      });
      return getLocalizedText(key, values);
    }),
    ...extraKeys.map(key => getLocalizedText(key)),
  ].join(" ");
};

// The settlement as stored right now, read without touching the store: the
// settlement wizard renders from its own copy until it leaves.
export const fetchStoredSettlement = async (
  settlementId: string,
): Promise<Settlement> => {
  const response = await settlementApi.getDetail({ id: settlementId });
  return settlementFromApi(unwrapEntity<ISettlement>(response.data));
};
